import type * as React from "react";

import { CalendarIcon } from "lucide-react";

import { invoke } from "../lib/utils";
import type { BasePickerOnChangeData } from "../pickers/BasePicker";
import MonthRangePicker from "../pickers/monthPicker/MonthRangePicker";
import InputView from "../views/InputView";
import BaseInput, {
  type BaseInputProps,
  type BaseInputState,
  type DateRelatedProps,
  type MinMaxValueProps,
} from "./BaseInput";
import {
  buildValue,
  dateValueToString,
  parseDatesRange,
  parseValue,
} from "./parse";
import { validateDatesRangeValue } from "./validate";

const DATES_SEPARATOR = " - ";

export type MonthRangeInputProps = BaseInputProps &
  DateRelatedProps &
  MinMaxValueProps;

export type MonthRangeInputOnChangeData = MonthRangeInputProps;

class MonthRangeInput extends BaseInput<MonthRangeInputProps, BaseInputState> {
  public static readonly defaultProps = {
    ...BaseInput.defaultProps,
    dateFormat: "MM-YYYY",
    icon: CalendarIcon,
  };

  constructor(props: MonthRangeInputProps) {
    super(props);
    this.state = { popupIsClosed: true };
  }

  public render() {
    const {
      value,
      dateFormat,
      initialDate: _initialDate,
      maxDate: _maxDate,
      minDate: _minDate,
      closable: _closable,
      localization,
      pickerWidth: _pickerWidth,
      pickerStyle: _pickerStyle,
      inline: _inline,
      tabIndex: _tabIndex,
      onChange: _onChange,
      ...fieldProps
    } = this.props;

    return (
      <InputView
        popupIsClosed={this.state.popupIsClosed}
        {...fieldProps}
        value={dateValueToString(value, dateFormat, localization)}
        onMount={this.onInputViewMount}
        closePopup={this.closePopup}
        openPopup={this.openPopup}
        onChange={this.onInputValueChange}
        validate={this.validate}
        renderPicker={this.getPicker}
      />
    );
  }

  private validate = (raw: string) => {
    const { dateFormat, localization, minDate, maxDate } = this.props;
    return validateDatesRangeValue(raw, {
      dateFormat: dateFormat || "MM-YYYY",
      localization,
      minDate,
      maxDate,
      separator: DATES_SEPARATOR,
      unit: "month",
      allowSameEndDate: true,
    });
  };

  private onInputValueChange = (
    e: React.SyntheticEvent<HTMLElement>,
    { value }: { value: string },
  ) => {
    invoke(this.props, "onChange", e, { ...this.props, value });
  };

  private getPicker = () => {
    const { value, dateFormat, initialDate, maxDate, minDate, localization } =
      this.props;
    const { start, end } = parseDatesRange(value as string, dateFormat);

    return (
      <MonthRangePicker
        isPickerInFocus={this.isPickerInFocus}
        isTriggerInFocus={this.isTriggerInFocus}
        inline={this.props.inline}
        onCalendarViewMount={this.onCalendarViewMount}
        closePopup={this.closePopup}
        onChange={this.handleSelect}
        dateFormat={dateFormat!}
        initializeWith={buildValue(
          start,
          initialDate,
          localization,
          dateFormat,
        )}
        start={start}
        end={end}
        minDate={parseValue(minDate, dateFormat, localization)}
        maxDate={parseValue(maxDate, dateFormat, localization)}
        localization={localization}
        onHeaderClick={() => undefined}
      />
    );
  };

  private handleSelect = (
    e: React.SyntheticEvent<HTMLElement> | null,
    { value }: BasePickerOnChangeData,
  ) => {
    const { dateFormat } = this.props;
    const { start, end } = value;
    let outputString = "";
    if (start && end) {
      outputString = `${start.format(dateFormat!)}${DATES_SEPARATOR}${end.format(dateFormat!)}`;
    } else if (start) {
      outputString = `${start.format(dateFormat!)}${DATES_SEPARATOR}`;
    }

    invoke(this.props, "onChange", e, {
      ...this.props,
      value: outputString,
      date: value,
    });
    if (this.props.closable && start && end) {
      this.closePopup();
    }
  };
}

export default MonthRangeInput;
